// ANANT ASM · Daily Story Auto-Post Kit
// Client-side canvas-based WhatsApp Status card generator. Zero-server-cost.
// 7 rotating themes; picks today's theme so users get a fresh design daily.
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';

const THEMES = [
  { id: 'morning',  name: '☀️ Morning Namaste',   bg: ['#f97316', '#facc15'], accent: '#fff8e7', headline: 'Suprabhat 🙏', tagline: 'Aaj bhi khaas offer aapke liye!' },
  { id: 'festive',  name: '🎉 Festive Special',   bg: ['#dc2626', '#f59e0b'], accent: '#fff1e6', headline: '🎊 Festive Dhamaka!', tagline: 'Aaj sab kuch extra special.' },
  { id: 'newarrival', name: '✨ New Arrival',      bg: ['#7c3aed', '#ec4899'], accent: '#fdf2ff', headline: '✨ Fresh Stock Aa Gaya!', tagline: 'Ekdum brand new · limited pieces.' },
  { id: 'thankyou', name: '💖 Thank You',          bg: ['#059669', '#0ea5e9'], accent: '#effefb', headline: 'Shukriya Parivaar 💖', tagline: 'Aapke pyaar se hum khilte hain.' },
  { id: 'weekend',  name: '🛍️ Weekend Offer',     bg: ['#0ea5e9', '#8b5cf6'], accent: '#ecfeff', headline: 'Weekend Bumper Sale!', tagline: 'Sirf 48 ghante ka mahotsav.' },
  { id: 'milestone', name: '🏆 Milestone',         bg: ['#0f172a', '#f59e0b'], accent: '#fef3c7', headline: '🏆 Milestone Achieved', tagline: 'Aap sab ka dhanyawaad!' },
  { id: 'today_only', name: '⚡ Today Only',        bg: ['#be123c', '#f43f5e'], accent: '#fff1f2', headline: '⚡ Aaj hi Aaj!', tagline: 'Kal mile na mile · aaj le lo.' },
];

// Cache Web Fonts note: Canvas will fall back to system fonts.

function drawStoryCanvas(ctx, W, H, theme, p, publicUrl) {
  // Vertical gradient background
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, theme.bg[0]);
  g.addColorStop(1, theme.bg[1]);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Decorative circles
  ctx.globalAlpha = 0.15;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath(); ctx.arc(W * 0.15, H * 0.18, 220, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(W * 0.9, H * 0.85, 280, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;

  // Top brand strip
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(0, 60, W, 96);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 44px system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText((p.business_name || 'My Shop').toUpperCase(), W / 2, 122);

  // Headline
  ctx.fillStyle = theme.accent;
  ctx.font = 'bold 108px system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  wrapText(ctx, theme.headline, W / 2, H * 0.32, W - 120, 118);

  // Tagline
  ctx.fillStyle = '#ffffff';
  ctx.font = '500 54px system-ui, -apple-system, "Segoe UI", sans-serif';
  wrapText(ctx, theme.tagline, W / 2, H * 0.52, W - 160, 66);

  // Merchant tagline (custom)
  if (p.tagline) {
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = 'italic 40px system-ui, -apple-system, sans-serif';
    wrapText(ctx, `"${p.tagline}"`, W / 2, H * 0.66, W - 160, 50);
  }

  // Card link footer
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(0, H - 220, W, 220);
  ctx.fillStyle = theme.accent;
  ctx.font = 'bold 42px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('📱 Visit our digital card', W / 2, H - 150);
  ctx.font = 'bold 34px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(publicUrl.replace(/^https?:\/\//, ''), W / 2, H - 90);
  ctx.font = '500 24px system-ui, -apple-system, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fillText('Powered by Anant Web Portal', W / 2, H - 40);
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || '').split(' ');
  let line = '';
  let cy = y;
  for (let n = 0; n < words.length; n++) {
    const test = line + words[n] + ' ';
    if (ctx.measureText(test).width > maxWidth && n > 0) {
      ctx.fillText(line.trim(), x, cy);
      line = words[n] + ' ';
      cy += lineHeight;
    } else { line = test; }
  }
  ctx.fillText(line.trim(), x, cy);
}

// Deterministic daily theme rotation
function pickTodayTheme() {
  const day = new Date();
  const idx = (day.getFullYear() * 373 + day.getMonth() * 31 + day.getDate()) % THEMES.length;
  return THEMES[idx];
}

// Same deterministic seed but for any given date — used by 7-day scheduler
function pickThemeForDate(date) {
  const idx = (date.getFullYear() * 373 + date.getMonth() * 31 + date.getDate()) % THEMES.length;
  return THEMES[idx];
}

// ============ 7-DAY SCHEDULER — client-side canvas queue ============
function ScheduledDayCard({ date, theme, profile, publicUrl, isToday }) {
  const cvRef = useRef(null);
  React.useEffect(() => {
    if (!cvRef.current) return;
    const W = 1080, H = 1920;
    cvRef.current.width = W; cvRef.current.height = H;
    drawStoryCanvas(cvRef.current.getContext('2d'), W, H, theme, profile || {}, publicUrl || 'anantwebportal.com');
  }, [theme, profile, publicUrl]);
  const label = date.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
  const filename = `${theme.id}-${date.toISOString().slice(0, 10)}.png`;
  const dl = () => {
    cvRef.current.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`✓ ${label} downloaded`);
    }, 'image/png', 0.95);
  };
  const share = () => {
    cvRef.current.toBlob(async (blob) => {
      const file = new File([blob], filename, { type: 'image/png' });
      const text = `${theme.headline}\n${theme.tagline}\n\n📱 ${publicUrl || ''}`.trim();
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text, title: profile?.business_name || 'My Shop' }); return; } catch { /* fall through */ }
      }
      dl();
      setTimeout(() => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'), 400);
    }, 'image/png', 0.95);
  };
  return (
    <div className={`rounded-xl border-2 ${isToday ? 'border-fuchsia-400' : 'border-slate-700'} bg-slate-900/60 p-2 flex flex-col items-center`} data-testid={`asm-story-day-${date.toISOString().slice(0,10)}`}>
      <div className="text-[10px] font-black text-center mb-1">
        {isToday ? <span className="text-fuchsia-300">Today · {label}</span> : <span className="opacity-90">{label}</span>}
      </div>
      <div className="rounded overflow-hidden border border-slate-700" style={{ width: 96 }}>
        <canvas ref={cvRef} style={{ width: '100%', height: 'auto', display: 'block' }} />
      </div>
      <div className="text-[9px] opacity-80 mt-1 text-center leading-tight">{theme.name}</div>
      <div className="flex gap-1 mt-1.5">
        <button onClick={share} data-testid={`asm-story-share-${date.toISOString().slice(0,10)}`} className="text-[9px] px-1.5 py-0.5 rounded bg-green-500 text-black font-black">📲</button>
        <button onClick={dl} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700 text-white font-black">💾</button>
      </div>
    </div>
  );
}

export default function ASMStoryKit({ profile, publicUrl }) {
  const [selected, setSelected] = useState(pickTodayTheme().id);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOverrides, setAiOverrides] = useState({}); // { [themeId]: { headline, tagline } }
  const canvasRef = useRef(null);
  const p = profile || {};
  const baseTheme = useMemo(() => THEMES.find((t) => t.id === selected) || THEMES[0], [selected]);
  const theme = useMemo(() => {
    const ov = aiOverrides[selected];
    return ov ? { ...baseTheme, headline: ov.headline || baseTheme.headline, tagline: ov.tagline || baseTheme.tagline } : baseTheme;
  }, [baseTheme, aiOverrides, selected]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = 1080, H = 1920;
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');
    drawStoryCanvas(ctx, W, H, theme, p, publicUrl || 'anantwebportal.com');
  }, [theme, p, publicUrl]);

  React.useEffect(() => { render(); }, [render]);

  const generateAiCaption = async () => {
    setAiBusy(true);
    try {
      const r = await api.post('/asm/story/ai-caption', {
        theme_id: baseTheme.id,
        theme_name: baseTheme.name,
        business_name: p.business_name || '',
        industry: p.industry || p.tagline || '',
        city: p.city || '',
        tagline: p.tagline || '',
      });
      setAiOverrides((cur) => ({ ...cur, [baseTheme.id]: { headline: r.data.headline, tagline: r.data.tagline } }));
      toast.success('✨ AI ne aaj ka fresh caption bana diya');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI caption failed');
    } finally { setAiBusy(false); }
  };

  const resetAi = () => {
    setAiOverrides((cur) => { const next = { ...cur }; delete next[baseTheme.id]; return next; });
    toast.success('Original caption restored');
  };

  const toBlob = () => new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png', 0.95));

  const shareOrDownload = async () => {
    setBusy(true);
    try {
      const blob = await toBlob();
      const file = new File([blob], `${theme.id}-story-${Date.now()}.png`, { type: 'image/png' });
      const canWebShare = typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] });
      const text = `${theme.headline}\n${theme.tagline}\n\n📱 ${publicUrl || ''}`.trim();
      if (canWebShare) {
        try {
          await navigator.share({ files: [file], text, title: p.business_name || 'My Shop' });
          toast.success('Shared! WhatsApp Status me daal do 🚀');
          return;
        } catch (_) { /* user cancelled — fall back to download */ }
      }
      // Fallback: trigger download + open WhatsApp with text-only prefill
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = file.name; a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded! Ab WhatsApp Status pe attach kar do 📲');
      setTimeout(() => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'), 400);
    } catch (e) {
      toast.error('Story banate hue error');
    } finally { setBusy(false); }
  };

  const downloadOnly = async () => {
    const blob = await toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${theme.id}-story-${Date.now()}.png`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  return (
    <div className="space-y-3" data-testid="asm-story-kit">
      <div className="rounded-2xl border-2 border-fuchsia-400/40 bg-slate-800/40 p-3">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black mb-1">🎨 Daily Story Kit · WhatsApp Status Ready</div>
        <div className="text-[11px] opacity-80 mb-2">Har din ka fresh design · 7 themes rotate · 1 tap share on WhatsApp Status. Aaj ka pick highlighted 👇</div>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
          {THEMES.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelected(t.id)}
              data-testid={`asm-story-theme-${t.id}`}
              className={`px-2 py-2 rounded-lg text-[10px] font-black transition ${selected === t.id ? 'bg-fuchsia-500 text-white ring-2 ring-fuchsia-300' : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-fuchsia-500/50'}`}
              style={selected === t.id ? { boxShadow: `0 6px 24px ${t.bg[0]}55` } : {}}
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>

      {/* Live preview — display-scaled */}
      <div className="flex flex-col items-center">
        <div className="rounded-xl overflow-hidden border-2 border-fuchsia-400/40 shadow-2xl shadow-fuchsia-500/30" style={{ width: 240 }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: 'auto', display: 'block' }} />
        </div>
        <div className="text-[10px] opacity-70 mt-2">1080×1920 · WhatsApp Status size</div>
      </div>

      <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-2 flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-0 text-[10px] text-amber-100/90">
          <div className="font-black text-amber-200">✨ AI Caption Booster</div>
          <div className="opacity-80">Aaj ka theme ({baseTheme.name}) aur aapki industry ke hisab se AI Hinglish caption likhega — ek tap.</div>
          {aiOverrides[baseTheme.id] && (
            <div className="mt-1 opacity-90 truncate">→ <b>{aiOverrides[baseTheme.id].headline}</b> · {aiOverrides[baseTheme.id].tagline}</div>
          )}
        </div>
        <button onClick={generateAiCaption} disabled={aiBusy} data-testid="asm-story-ai-caption" className="px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-black disabled:opacity-50">
          {aiBusy ? '⏳ Writing…' : '✨ AI Caption'}
        </button>
        {aiOverrides[baseTheme.id] && (
          <button onClick={resetAi} className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-600 text-slate-200 text-[10px] font-black">↺ Reset</button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={shareOrDownload}
          disabled={busy}
          data-testid="asm-story-share"
          className="px-3 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-black disabled:opacity-50"
        >
          {busy ? '⏳ Preparing…' : '📲 Share on WhatsApp Status'}
        </button>
        <button
          onClick={downloadOnly}
          data-testid="asm-story-download"
          className="px-3 py-3 rounded-xl bg-slate-800 border border-slate-600 text-white text-xs font-black"
        >
          💾 Download PNG
        </button>
      </div>

      {/* 📆 7-DAY QUEUE — auto-generated theme rotation for the coming week */}
      <div className="rounded-2xl border-2 border-cyan-400/40 bg-cyan-500/5 p-3">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-1">📆 7-Day Story Queue</div>
        <div className="text-[11px] opacity-80 mb-3">Aage 7 din ka schedule ekdum ready — har din alag theme rotate hoga. Advance me download karke offline bhi post kar sakte ho.</div>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="asm-story-queue">
          {Array.from({ length: 7 }).map((_, i) => {
            const d = new Date();
            d.setDate(d.getDate() + i);
            const th = pickThemeForDate(d);
            return <ScheduledDayCard key={i} date={d} theme={th} profile={p} publicUrl={publicUrl} isToday={i === 0} />;
          })}
        </div>
        <div className="text-[10px] opacity-60 mt-2 text-center">💡 Tip: 7 din ek saath download karke phone gallery me rakh do — 1 week ka social media plan ready!</div>
      </div>
    </div>
  );
}
